import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { getPortal } from '@command-center/portal-registry';
import {
  AppShell,
  AuthLinkError as SharedAuthLinkError,
  AuthScreen,
  Button,
  type ResolvedBranding,
  type RouteTitle,
} from '@command-center/ui-system';
import { getBranding } from '../../platform/branding';
import { getClientModules, getClientNavigationGroups } from '../../platform/modules';
import { config } from '../../portal.config';
import { useAuth } from '../../auth/AuthProvider';
import { SignInForm } from '../../auth/SignInForm';
import { ResetPasswordForm } from '../../auth/ResetPasswordForm';
import { ClientWorkspaceMenu } from './ClientWorkspaceMenu';
import {
  ClientGlobalShortcuts,
  ClientSidebarCommandButton,
} from './ClientCommandLauncher';
import { useOrganization } from '../../organization/OrganizationProvider';
import { MessagesProvider } from '../../messages/MessagesProvider';
import { exitBrowserImpersonation } from '@command-center/auth-sdk/browser-impersonation';
import { hasClientPortalCapability } from '@oneup/shared';
import { RightSidebarProvider, useRightSidebar } from '@command-center/oneup-portal-ui';

const ClientModuleView = lazy(() => import('../module/ClientModuleView').then((module) => ({
  default: module.ClientModuleView,
})));
const ClientSettingsView = lazy(() => import('../settings/ClientSettingsViews').then((module) => ({
  default: module.ClientSettingsView,
})));
const ClientBillingManagement = lazy(() => import('../features/ClientBillingManagement').then(module => ({ default: module.ClientBillingManagement })));
const StandardHostingSignup = lazy(() => import('../features/ClientBillingManagement').then(module => ({ default: module.StandardHostingSignup })));
const ClientSubscriptionOffer = lazy(() => import('../features/ClientSubscriptionOffers').then(module => ({ default: module.ClientSubscriptionOffer })));
const ClientInvoiceDetail = lazy(() => import('../features/ClientInvoiceDetail').then(module => ({ default: module.ClientInvoiceDetail })));
const ClientAlicePanel = lazy(() => import('./ClientAlicePanel').then((module) => ({
  default: module.ClientAlicePanel,
})));
const ClientNotificationsBell = lazy(() => import('./ClientNotificationsBell').then((module) => ({
  default: module.ClientNotificationsBell,
})));
const ClientCommandPalette = lazy(() => import('./ClientCommandPalette').then((module) => ({
  default: module.ClientCommandPalette,
})));

const VITE_API_BASE_URL = (
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL ?? ''
).trim();

/**
 * Standard Client Portal shell.
 *
 * Adopts the current shared workspace shell, including its collapsible grouped
 * navigation and sidebar workspace control. The organization picker remains a
 * display/control surface only; every API request is independently authorized.
 */
export function ClientShell() {
  return (
    <RightSidebarProvider storageKey={`${config.appId}:right-sidebar`}>
      <ClientShellContent />
    </RightSidebarProvider>
  );
}

function ClientShellContent() {
  const rightSidebar = useRightSidebar();
  const branding = useMemo(() => getBranding(), []);
  const {
    user,
    actor,
    impersonation,
    impersonationNotificationEditing,
    setImpersonationNotificationEditing,
    isLoading,
    signOut,
    accessState,
    accessError,
    passwordEmailAction,
    emailAuthPortal,
    emailAuthComplete,
    emailAuthError,
  } = useAuth();
  const accountPortal = getPortal(emailAuthComplete?.portalKey ?? emailAuthPortal ?? 'oneup');
  const accountBranding = accountPortal.branding;
  const organization = useOrganization();
  const selectedOrganization = organization.organizations.find(
    (item) => item.clientId === organization.selectedClientId,
  );
  const capabilities = selectedOrganization?.capabilities;
  const groups = useMemo(() => getClientNavigationGroups(capabilities), [capabilities]);
  const aliceIncluded = hasClientPortalCapability(capabilities, 'alice_chat');
  const searchIncluded = hasClientPortalCapability(capabilities, 'search');
  const head = useRouteTitle();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [commandPaletteInitialQuery, setCommandPaletteInitialQuery] = useState('');
  const openCommandPalette = useCallback((initialQuery = '') => {
    setCommandPaletteInitialQuery(initialQuery);
    setCommandPaletteOpen(true);
  }, []);

  if (isLoading) {
    return <CenteredMessage title="Signing you in…" />;
  }

  if (emailAuthError) {
    return <AuthLinkError branding={accountBranding} message={emailAuthError} />;
  }

  if (passwordEmailAction && user) {
    return (
      <AuthScreen
        branding={accountBranding}
        description={`Securely finish setting up your ${accountPortal.appName} account.`}
      >
        <ResetPasswordForm action={passwordEmailAction} />
      </AuthScreen>
    );
  }

  if (emailAuthComplete) {
    return (
      <AuthScreen
        branding={accountBranding}
        description={`Your ${accountPortal.appName} account is ready.`}
      >
        <div className="grid gap-5 text-center">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              {emailAuthComplete.action === 'invite' ? 'Account setup complete' : 'Password updated'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Sign in normally to continue. Your temporary account-management session has been closed.
            </p>
          </div>
          <Button
            className="h-11 w-full"
            onClick={() => window.location.assign(accountPortal.postAuthUrl)}
          >
            Continue to {accountPortal.branding.shortLabel}
          </Button>
        </div>
      </AuthScreen>
    );
  }

  if (!user && !impersonation) {
    return (
      <AuthScreen
        branding={branding}
        description="See everything One Up is doing for your business in one secure workspace."
      >
        <SignInForm />
      </AuthScreen>
    );
  }

  if (accessState === 'checking') return <CenteredMessage title="Opening your workspace…" />;
  if (accessState === 'denied') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <section className="max-w-md rounded-lg border border-border bg-card p-6 text-center">
          <h1 className="text-lg font-semibold">You don’t have access yet</h1>
          <p className="mt-2 text-sm text-muted-foreground">{accessError}</p>
          <Button className="mt-4" variant="outline" onClick={() => void signOut()}>Sign out</Button>
        </section>
      </div>
    );
  }
  /*
   * The organization list arrives with the access decision, so `isLoading` is
   * normally false and this never renders. It remains only for the rollout case
   * where the browser bundle is newer than the Worker and the list has to be
   * fetched separately.
   */
  if (organization.isLoading) return <CenteredMessage title="Loading your workspace…" />;
  if (organization.error || organization.organizations.length === 0 || !organization.selectedClientId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <section className="max-w-md rounded-lg border border-border bg-card p-6 text-center">
          <h1 className="text-lg font-semibold">No workspace linked yet</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {organization.error ?? 'This account isn’t connected to a business yet. Contact One Up and we’ll finish setting you up.'}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button variant="outline" onClick={organization.reload}>Try again</Button>
            <Button variant="outline" onClick={() => void signOut()}>Sign out</Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <MessagesProvider onForceOpen={rightSidebar.expand}>
    {impersonation && (
      <div className="sticky top-0 z-[100] flex flex-wrap items-center justify-between gap-2 border-b border-amber-400/50 bg-amber-100 px-4 py-2 text-sm text-amber-950">
        <strong>
          Viewing as {impersonation.impersonation.targetName} for {impersonation.impersonation.organizationName} — {impersonationNotificationEditing ? 'Notification Editing' : 'Read Only'}
        </strong>
        <span>
          {impersonation.impersonation.portalLabel} · expires {new Date(impersonation.impersonation.expiresAt).toLocaleTimeString()}
        </span>
        <Button
          size="sm"
          variant="outline"
          aria-pressed={impersonationNotificationEditing}
          onClick={() => setImpersonationNotificationEditing(!impersonationNotificationEditing)}
        >
          {impersonationNotificationEditing ? 'Return to read only' : 'Enable notification editing'}
        </Button>
        {impersonationNotificationEditing && (
          <span>Only notification preferences can be saved. Other changes remain read-only.</span>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => void exitBrowserImpersonation(
            VITE_API_BASE_URL,
            `https://admin.oneuphi.com/users/${encodeURIComponent(impersonation.impersonation.targetAppUserId)}`,
          )}
        >
          Exit Impersonation
        </Button>
      </div>
    )}
    <AppShell
      branding={branding}
      groups={groups}
      head={head}
      headerVariant="workspace"
      collapsibleNavGroups={false}
      navGroupStorageKey={`${config.appId}:nav-groups`}
      collapsibleSidebar
      sidebarStorageKey={`${config.appId}:sidebar`}
      sidebarCommand={({ compact }) => (
        <ClientSidebarCommandButton
          compact={compact}
          onOpen={() => openCommandPalette(searchIncluded ? '' : '/')}
          searchEnabled={searchIncluded}
        />
      )}
      sidebarWorkspace={({ compact }) => (
        <ClientWorkspaceMenu
          compact={compact}
          userName={actor?.name ?? undefined}
          userEmail={actor?.email ?? user?.email ?? undefined}
          onSignOut={() => void signOut()}
        />
      )}
      actions={<Suspense fallback={null}><ClientNotificationsBell /></Suspense>}
      rightSidebar={<Suspense fallback={null}><ClientAlicePanel aliceEnabled={aliceIncluded} /></Suspense>}
      mobileRightSidebar
      rightSidebarLabel="Alice, Record Details, and Messages"
      rightSidebarOpenToken={rightSidebar.openToken}
      overlays={
        <>
          <ClientGlobalShortcuts onOpen={openCommandPalette} />
          {commandPaletteOpen && (
            <Suspense fallback={null}>
              <ClientCommandPalette
                open
                initialQuery={commandPaletteInitialQuery}
                onOpenChange={setCommandPaletteOpen}
                capabilities={capabilities}
              />
            </Suspense>
          )}
        </>
      }
      contentClassName="px-4 pt-4 sm:px-5 sm:pt-5 lg:px-6 lg:pt-6"
      sidebarFooter={
        <span>Powered by One Up</span>
      }
    >
      {/*
       * A build shipped without an API base cannot load anything. The client
       * cannot fix that, so the notice tells them who can rather than naming
       * the variable.
       */}
      {VITE_API_BASE_URL === '' && (
        <div className="mb-4 rounded-lg border border-warning/40 bg-warning/5 p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">Your portal isn’t finished setting up.</strong> Please let
          the One Up team know — we’ll have it sorted shortly.
        </div>
      )}
      <ClientRoutes />
    </AppShell>
    </MessagesProvider>
  );
}

export function ClientRoutes() {
  const organization = useOrganization();
  const selected = organization.organizations.find(
    (item) => item.clientId === organization.selectedClientId,
  );
  const modules = getClientModules(selected?.capabilities);
  const work = modules.find((module) => module.id === 'work');
  const forms = modules.find((module) => module.id === 'forms');
  const websites = modules.find((module) => module.id === 'websites');
  const docs = modules.find((module) => module.id === 'docs');
  const subscriptions = modules.find((module) => module.id === 'subscriptions');
  const invoices = modules.find((module) => module.id === 'invoices');
  const organizations = modules.find((module) => module.id === 'organizations');
  return (
    <Suspense fallback={<div className="p-6 text-sm text-muted-foreground" aria-live="polite">Loading workspace…</div>}>
    <Routes>
      <Route path="/billing" element={<ClientBillingManagement />} />
      <Route path="/billing/offers/:offerId" element={<ClientSubscriptionOffer />} />
      {invoices && <Route path="/invoices/:recordId" element={<ClientInvoiceDetail />} />}
      <Route path="/domains" element={<ClientBillingManagement initialTab="domains" />} />
      <Route path="/hosting" element={<StandardHostingSignup />} />
      {/* The organization workspace is the portal home. The retired Homebase
          deep link and the bare root both land on its Overview. */}
      <Route path="/" element={<Navigate to="/organizations/overview" replace />} />
      {modules.filter((module) => module.id !== 'alice-activity').map((module) => {
        const element = <ClientModuleView module={module} />;
        const hasDetail = module.view !== 'homebase' && Boolean(module.detail);
        return (
          <Route key={module.id}>
            <Route path={module.route} element={element} />
            {hasDetail && <Route path={`${module.route}/:recordId`} element={element} />}
          </Route>
        );
      })}
      {work && <>
        {/* Tasks is the default tab, so /work/tasks is the module route. These
            keep every pre-existing link and bookmark working. */}
        <Route path="/work" element={<Navigate to="/work/tasks" replace />} />
        <Route path="/work/projects" element={<ClientModuleView module={work} />} />
        <Route path="/work/projects/:recordId" element={<ClientModuleView module={work} />} />
        <Route path="/work/tasks/:recordId" element={<ClientModuleView module={work} />} />
        <Route path="/work/done" element={<ClientModuleView module={work} />} />
        <Route path="/work/done/:recordId" element={<ClientModuleView module={work} />} />
      </>}
      {forms && <>
        <Route path="/forms/:recordId" element={<ClientModuleView module={forms} />} />
        <Route path="/forms/entries" element={<ClientModuleView module={forms} />} />
        <Route path="/forms/entries/:recordId" element={<ClientModuleView module={forms} />} />
      </>}
      {websites && <Route path="/websites/:recordId" element={<ClientModuleView module={websites} />} />}
      {subscriptions && (
        <Route path="/subscriptions/:recordId" element={<ClientModuleView module={subscriptions} />} />
      )}
      {docs && <Route path="/docs/:recordId" element={<ClientModuleView module={docs} />} />}
      <Route path="/organizations" element={<Navigate to="/organizations/overview" replace />} />
      <Route path="/organizations/projects" element={<Navigate to="/work/tasks" replace />} />
      <Route path="/organizations/websites" element={<Navigate to="/websites" replace />} />
      <Route path="/organizations/agents" element={<Navigate to="/ai-systems/agents" replace />} />
      <Route path="/organizations/team" element={<Navigate to="/settings?tab=access" replace />} />
      <Route path="/organizations/files" element={<Navigate to="/docs" replace />} />
      <Route path="/organizations/billing" element={<Navigate to="/invoices" replace />} />
      <Route path="/organizations/communications" element={<Navigate to="/organizations/overview#updates" replace />} />
      <Route path="/organizations/switches" element={<Navigate to="/switchboard" replace />} />
      {organizations && <Route path="/organizations/:organizationTab" element={<Navigate to="/organizations/overview" replace />} />}
      <Route path="/settings" element={<ClientSettingsView />} />
      <Route path="/settings/user" element={<Navigate to="/settings?tab=account" replace />} />
      <Route path="/settings/workspace" element={<Navigate to="/settings" replace />} />
      <Route path="/settings/connections" element={<Navigate to="/settings" replace />} />
      <Route path="/settings/notifications" element={<Navigate to="/settings?tab=notifications" replace />} />
      <Route path="/settings/alice" element={<Navigate to="/settings" replace />} />
      <Route path="/ai-systems/alice-activity" element={<Navigate to="/settings?tab=ai-usage" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function useRouteTitle(): RouteTitle {
  const head = useResolvedRouteTitle();
  // Keep the browser tab and history in step with the page. Without this every
  // entry in a client's history reads "One Up Portal", which makes going back
  // guesswork.
  useEffect(() => {
    document.title = `${head.title} · ${config.branding.name}`;
  }, [head.title]);
  return head;
}

function useResolvedRouteTitle(): RouteTitle {
  const { pathname } = useLocation();
  const modules = getClientModules();
  if (pathname.startsWith('/billing') || pathname.startsWith('/invoices') || pathname.startsWith('/subscriptions')) {
    return { title: 'Billing', sub: config.branding.name };
  }
  if (pathname.startsWith('/ai-systems/agents') || pathname.startsWith('/ai-systems/calls')) {
    return { title: 'AI Voice Agents', sub: config.branding.name };
  }
  if (pathname.startsWith('/settings')) {
    return { title: 'Settings', sub: config.branding.name };
  }
  const routeAliases: Record<string, string> = {
    '/work': 'work',
    '/organizations': 'organizations',
  };
  const aliasedModuleId = Object.entries(routeAliases).find(([prefix]) =>
    pathname === prefix || pathname.startsWith(`${prefix}/`),
  )?.[1];
  const aliasedMatch = modules.find((module) => module.id === aliasedModuleId);
  const match = modules
    .filter((module) => pathname === module.route || pathname.startsWith(`${module.route}/`))
    .sort((a, b) => b.route.length - a.route.length)[0];
  return { title: aliasedMatch?.label ?? match?.label ?? config.clientName, sub: config.branding.name };
}

function NotFound() {
  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-base font-semibold text-foreground">Page not found</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        That page doesn’t exist, or isn’t part of your portal. Use the menu on the left to get back on track.
      </p>
    </section>
  );
}

function CenteredMessage({ title }: { title: string }) {
  return (
    <div className="flex h-screen items-center justify-center bg-background text-sm text-muted-foreground">
      {title}
    </div>
  );
}

function AuthLinkError({
  branding,
  message,
}: {
  branding: ResolvedBranding;
  message: string;
}) {
  return (
    <AuthScreen branding={branding} description="Secure access to your One Up workspace.">
      <SharedAuthLinkError
        message={message}
        onReturn={() => {
          window.history.replaceState({}, '', '/');
          window.location.reload();
        }}
      />
    </AuthScreen>
  );
}
